import { useEffect } from 'react';
import { enableGoogleOauthRedirect } from '@gonfalon/dogfood-flags/dogfood-flags';
import { trackError } from '@gonfalon/telemetry/trackError';
import { noop } from 'lodash';

import { setGoogleOAuthRedirect } from '#utils/googleOAuthRedirectStorage';
import { trackLoginForm } from '#utils/loginUtils';

import { OAuthButtonContainer } from '../OAuthButtonContainer';

export enum GoogleDataText {
  SIGN_IN = 'signin',
  SIGNIN_WITH = 'signin_with',
  SIGNUP_WITH = 'signup_with',
  CONTINUE_WITH = 'continue_with',
}

export type GoogleOAuthProps = {
  clientId: string;
  dataText?: GoogleDataText;
  enableOneTap?: boolean;
  redirect?: string;
  width?: number;
  theme?: 'filled_black' | 'outline';
  logoAlignment?: 'left' | 'center';
  onClick?: VoidFunction;
};

export const GoogleOAuthButton = ({
  clientId,
  dataText,
  enableOneTap = true,
  redirect = '',
  width = 700,
  theme = 'filled_black',
  logoAlignment = 'left',
  onClick = noop,
}: GoogleOAuthProps) => {
  let decodedRedirectPath = '';
  try {
    decodedRedirectPath = decodeURIComponent(redirect);
  } catch (error) {
    decodedRedirectPath = '/';
    if (error instanceof Error) {
      trackError(error, { severity: 'low' });
    }
  }

  // Google OAuth redirect_uri must be pre-registered and can't have dynamic query params.
  // So we use a simple redirect URI and store the actual redirect path in sessionStorage.
  const loginUri = `${window.location.origin}/oauth/google`;

  useEffect(() => {
    // @ts-expect-error the gsi widget data-click_listener only accepts a global function name. In order to handle the
    // click event we have to set the handler on window. https://github.com/google/recaptcha/issues/74
    window.onClickGoogleOauth = () => {
      if (onClick) {
        onClick();
      }
      trackLoginForm('Google OAuth Button Clicked');

      // Store the redirect URL before redirecting to Google OAuth. Include window.location.search
      // so query params (e.g. for Slack app authorization) survive the OAuth round-trip.
      if (enableGoogleOauthRedirect() && decodedRedirectPath) {
        setGoogleOAuthRedirect(`${decodedRedirectPath}${window.location.search}`);
      }
    };

    const scriptUrl = 'https://accounts.google.com/gsi/client';
    const scriptTags = document.querySelectorAll(`script[src="${scriptUrl}"]`);
    const script = document.createElement('script');

    if (scriptTags.length === 0) {
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;

      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, []);

  return (
    <OAuthButtonContainer>
      <div
        id="g_id_onload"
        data-context="use"
        data-ux_mode="redirect"
        data-client_id={clientId}
        data-login_uri={loginUri}
        data-auto_prompt={enableOneTap}
      ></div>

      <div
        className="g_id_signin"
        data-type="standard"
        data-shape="rectangular"
        data-theme={theme}
        data-size="large"
        data-logo_alignment={logoAlignment}
        data-text={dataText}
        data-width={width}
        data-test-id="google-oauth-login"
        data-click_listener="onClickGoogleOauth"
      ></div>
    </OAuthButtonContainer>
  );
};
